import React, { useRef } from 'react';
import { Upload, Trash2, Plus, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { useLanguage } from '@/contexts/LanguageContext';

export interface StickerItem {
  id: string;
  image: string;
  x: number; // position in % of canvas
  y: number;
  scale: number; // 0.1 to 3
  rotation: number; // degrees
}

interface StickerLibraryItem {
  id: string;
  image: string;
}

interface StickerOverlayProps {
  stickerLibrary: StickerLibraryItem[];
  stickersOnCanvas: StickerItem[];
  onAddStickerToLibrary: (file: File) => void;
  onPlaceStickerOnCanvas: (libraryStickerId: string) => void;
  onRemoveStickerFromLibrary: (id: string) => void;
  onUpdateSticker: (id: string, updates: Partial<StickerItem>) => void;
  onRemoveSticker: (id: string) => void;
}

const StickerOverlay: React.FC<StickerOverlayProps> = ({
  stickerLibrary,
  stickersOnCanvas,
  onAddStickerToLibrary,
  onPlaceStickerOnCanvas,
  onRemoveStickerFromLibrary,
  onUpdateSticker,
  onRemoveSticker,
}) => {
  const { t } = useLanguage();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      Array.from(files).forEach(file => {
        if (file.type.startsWith('image/')) {
          onAddStickerToLibrary(file);
        }
      });
    }
    // Reset input to allow re-uploading the same file
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-4">
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept="image/*"
        multiple
        onChange={handleFileSelect}
      />

      <Button
        variant="outline"
        className="w-full"
        onClick={() => fileInputRef.current?.click()}
      >
        <Upload className="w-4 h-4 mr-2" />
        {t.uploadSticker || 'Upload Sticker'}
      </Button>

      {stickerLibrary.length > 0 && (
        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground font-medium">
            Библиотека стикеров
          </Label>
          <div className="grid grid-cols-3 gap-2">
            {stickerLibrary.map((sticker) => (
              <div
                key={sticker.id}
                className="relative group aspect-square rounded-lg bg-muted/50 border border-border overflow-hidden"
              >
                <img
                  src={sticker.image}
                  alt="Sticker"
                  className="w-full h-full object-contain p-1"
                />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 bg-primary/90 hover:bg-primary text-primary-foreground"
                    onClick={() => onPlaceStickerOnCanvas(sticker.id)}
                    title="Добавить на холст"
                  >
                    <Check className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 bg-destructive/90 hover:bg-destructive text-destructive-foreground"
                    onClick={() => onRemoveStickerFromLibrary(sticker.id)}
                    title="Удалить"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {stickerLibrary.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-4">
          {t.noStickersYet || 'No stickers yet. Upload images to use as stickers.'}
        </p>
      )}

      {stickersOnCanvas.length > 0 && (
        <div className="space-y-3 pt-2 border-t border-border">
          <Label className="text-xs text-muted-foreground font-medium">
            Стикеры на холсте
          </Label>
          {stickersOnCanvas.map((sticker) => (
            <div
              key={sticker.id}
              className="p-3 rounded-lg bg-muted/50 border border-border space-y-3"
            >
              <div className="flex items-center gap-3">
                <img
                  src={sticker.image}
                  alt="Sticker"
                  className="w-10 h-10 object-contain rounded"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground truncate">
                    {t.stickerLabel || 'Sticker'} #{sticker.id.split('-').pop()?.slice(-4)}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onRemoveSticker(sticker.id)}
                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>

              <div className="space-y-2">
                <Label className="panel-label text-muted-foreground text-xs">
                  {t.stickerScale || 'Size'}: {Math.round(sticker.scale * 100)}%
                </Label>
                <Slider
                  value={[sticker.scale]}
                  onValueChange={([v]) => onUpdateSticker(sticker.id, { scale: v })}
                  min={0.1}
                  max={3}
                  step={0.05}
                />
              </div>

              <div className="space-y-2">
                <Label className="panel-label text-muted-foreground text-xs">
                  {t.stickerRotation || 'Rotation'}: {sticker.rotation}°
                </Label>
                <Slider
                  value={[sticker.rotation]}
                  onValueChange={([v]) => onUpdateSticker(sticker.id, { rotation: v })}
                  min={0}
                  max={360}
                  step={5}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {stickersOnCanvas.length > 0 && (
        <p className="text-xs text-muted-foreground text-center">
          {t.dragStickerOnCanvas || 'Drag stickers on the canvas to reposition'}
        </p>
      )}
    </div>
  );
};

export default StickerOverlay;
